import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotFoundPage from './NotFoundPage';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Brain, MapPin, Search, Loader2, BadgeCheck, Star, MessageCircle, Video, CreditCard, ChevronRight, Home } from 'lucide-react';
import { API } from '../utils/api';


const SPECIALTY_LABELS = {
  'terapeuta': 'Terapeutas',
  'psicologo': 'Psicólogos',
  'psiquiatra': 'Psiquiatras',
  'consejero': 'Consejeros',
  'especialista-adicciones': 'Especialistas en Adicciones'
};

const SPECIALTY_SINGULAR = {
  'terapeuta': 'Terapeuta',
  'psicologo': 'Psicólogo',
  'psiquiatra': 'Psiquiatra',
  'consejero': 'Consejero',
  'especialista-adicciones': 'Especialista en Adicciones'
};

function StarRating({ rating, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star} 
          className={`${sizeClass} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} 
        />
      ))}
    </div>
  );
}

function Breadcrumbs({ specialty, specialtyLabel, city, cityLabel }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6 flex-wrap">
      <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
        <Home className="w-4 h-4" />
        <span>Inicio</span>
      </Link>
      <ChevronRight className="w-4 h-4" />
      <Link to="/profesionales" className="hover:text-emerald-400 transition-colors">
        Profesionales
      </Link>
      {specialty && (
        <>
          <ChevronRight className="w-4 h-4" />
          {city ? (
            <Link to={`/profesional/${specialty}`} className="hover:text-emerald-400 transition-colors">
              {specialtyLabel}
            </Link>
          ) : (
            <span className="text-slate-200">{specialtyLabel}</span>
          )}
        </>
      )}
      {city && (
        <>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-200">{cityLabel}</span>
        </>
      )}
    </nav>
  );
}

function ProfessionalCard({ pro }) {
  const getTypeLabel = (v) => {
    const types = {
      'therapist': 'Terapeuta',
      'psychologist': 'Psicólogo',
      'psychiatrist': 'Psiquiatra',
      'counselor': 'Consejero'
    };
    return types[v] || v || 'Profesional';
  };

  return (
    <Link to={`/profesionales/${pro.user_id}`}>
      <Card className="h-full bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 group">
        <CardContent className="p-5">
          <div className="flex items-center gap-4 mb-3">
            {pro.picture || pro.profile_image ? (
              <img 
                src={pro.picture || pro.profile_image} 
                alt={pro.name} 
                className="w-14 h-14 rounded-xl object-cover ring-2 ring-slate-200 group-hover:ring-emerald-500/50 transition-all duration-300" 
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center">
                <Brain className="w-7 h-7 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base text-slate-800 group-hover:text-emerald-600 transition-colors duration-200 truncate">
                  {pro.name}
                </h3>
                {pro.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
              </div>
              <p className="text-sm text-emerald-600 font-medium">{getTypeLabel(pro.professional_type)}</p>
            </div>
          </div>
          
          {pro.specialization && (
            <div className="p-2 bg-slate-50 rounded-lg border border-slate-200 mb-3">
              <p className="text-sm text-slate-700 line-clamp-2 font-medium">{pro.specialization}</p>
            </div>
          )}
          
          {(pro.booking_price || pro.final_price || pro.consultation_price) && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <CreditCard className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">
                ${(pro.final_price || pro.booking_price || pro.consultation_price).toLocaleString('es-CL')}/sesión
              </span>
            </div>
          )}
          
          <div className="flex flex-wrap gap-1.5 mb-3">
            {pro.modality && (
              <span className="flex items-center gap-1 text-xs px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full font-medium border border-blue-200">
                <Video className="w-3 h-3" />
                {pro.modality === 'presencial' ? 'Presencial' : pro.modality === 'online' ? 'Online' : 'Presencial y Online'}
              </span>
            )}
            {pro.accepts_fonasa && (
              <span className="text-xs px-2.5 py-1 bg-teal-100 text-teal-800 rounded-full font-medium border border-teal-200">FONASA</span>
            )}
            {pro.accepts_isapre && (
              <span className="text-xs px-2.5 py-1 bg-cyan-100 text-cyan-800 rounded-full font-medium border border-cyan-200">ISAPRE</span>
            )}
          </div>
          
          {pro.average_rating > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <StarRating rating={Math.round(pro.average_rating)} size="sm" />
              <span className="text-sm text-slate-500">({pro.total_reviews})</span>
            </div>
          )}
          
          <div className="flex items-center gap-3 text-sm text-slate-500 mt-2">
            {pro.city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />{pro.city}
              </span>
            )}
            {pro.whatsapp && (
              <span className="flex items-center gap-1 text-green-600">
                <MessageCircle className="w-4 h-4" />WhatsApp
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function RelatedSearches({ specialty, city, filters }) {
  const navigate = useNavigate();
  
  if (!filters) return null;
  
  const { specialties = [], cities = [] } = filters;
  
  // Get other specialties
  const otherSpecialties = specialties.filter(s => s.slug !== specialty).slice(0, 6);
  
  // Get other cities (if we have a specialty)
  const otherCities = specialty ? cities.filter(c => c.slug !== city).slice(0, 10) : [];
  
  return (
    <div className="mt-12 space-y-8">
      {/* Other specialties in same city */}
      {city && otherSpecialties.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Otras especialidades en {filters.cities?.find(c => c.slug === city)?.label || city}
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherSpecialties.map((s) => (
              <Link
                key={s.slug}
                to={`/profesional/${s.slug}/${city}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {s.label} en {filters.cities?.find(c => c.slug === city)?.label || city}
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* Same specialty in other cities */}
      {specialty && otherCities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            {SPECIALTY_LABELS[specialty] || specialty} en otras ciudades
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/profesional/${specialty}/${c.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {c.label} ({c.count})
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* All specialties (if no specialty selected) */}
      {!specialty && specialties.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Buscar por especialidad
          </h3>
          <div className="flex flex-wrap gap-2">
            {specialties.map((s) => (
              <Link
                key={s.slug}
                to={`/profesional/${s.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {s.label} ({s.count})
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* All cities */}
      {specialty && !city && cities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            {SPECIALTY_LABELS[specialty] || specialty} por ciudad
          </h3>
          <div className="flex flex-wrap gap-2">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/profesional/${specialty}/${c.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {c.label} ({c.count})
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProfessionalsBySpecialty() {
  const { specialty } = useParams();
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [profRes, filtersRes] = await Promise.all([
          axios.get(`${API}/seo/profesionales/${specialty}?page=${page}&limit=12`),
          axios.get(`${API}/seo/available-filters`)
        ]);
        setProfessionals(profRes.data.professionals || []);
        setTotalPages(profRes.data.pages || 1);
        setTotal(profRes.data.total || 0);
        setData(profRes.data);
        setFilters(filtersRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [specialty, page]);

  const handleCityChange = (city) => {
    if (city && city !== 'all') {
      navigate(`/profesional/${specialty}/${city}`);
    }
    setSelectedCity(city);
  };

  // Validar specialty. Si no es válida → 404 con noindex (Soft 404 fix).
  if (!SPECIALTY_LABELS[specialty]) return <NotFoundPage />;

  const specialtyLabel = data?.specialty_label || SPECIALTY_LABELS[specialty] || specialty;
  const specialtySingular = SPECIALTY_SINGULAR[specialty] || specialty;

  const seoTitle = `${specialtyLabel} Especialistas en Adicciones en Chile | Sin Adicciones`;
  const seoDescription = `Encuentra ${specialtyLabel.toLowerCase()} especializados en tratamiento de adicciones en Chile. Directorio verificado con reseñas, precios y contacto directo.`;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://sinadicciones.org/profesional/${specialty}`} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href={`https://sinadicciones.org/profesional/${specialty}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": seoTitle,
          "description": seoDescription,
          "url": `https://sinadicciones.org/profesional/${specialty}`,
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": total,
            "itemListElement": professionals.slice(0, 10).map((pro, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "item": {
                "@type": "Person",
                "name": pro.name,
                "jobTitle": specialtySingular,
                "address": pro.city ? { "@type": "PostalAddress", "addressLocality": pro.city } : undefined
              }
            }))
          }
        })}</script>
      
      <Header />
      <main className="pt-20">
        <section className="bg-slate-900 border-b border-slate-800 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumbs 
              specialty={specialty} 
              specialtyLabel={specialtyLabel}
            />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3" data-testid="seo-title">
              {specialtyLabel} en Adicciones
            </h1>
            <p className="text-slate-400 text-lg">
              {total > 0 ? `${total} ${specialtyLabel.toLowerCase()} encontrados en Chile` : `${specialtyLabel} especializados en tratamiento de adicciones`}
            </p>
            
            {/* Quick city filter */}
            {filters?.cities && filters.cities.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                <span className="text-slate-400 text-sm">Filtrar por ciudad:</span>
                <Select value={selectedCity} onValueChange={handleCityChange}>
                  <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-slate-200">
                    <MapPin className="w-4 h-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Todas las ciudades" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-slate-300">Todas las ciudades</SelectItem>
                    {filters.cities.map((c) => (
                      <SelectItem key={c.slug} value={c.slug} className="text-slate-300">
                        {c.label} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : professionals.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <Brain className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">No se encontraron {specialtyLabel.toLowerCase()}</h3>
                <p className="text-slate-400 mb-6">Intenta buscar en otra especialidad</p>
                <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Link to="/profesionales">Ver todos los profesionales</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="professionals-seo-grid">
                  {professionals.map((pro) => (
                    <ProfessionalCard key={pro.user_id} pro={pro} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button 
                      variant="outline" 
                      onClick={() => setPage(p => Math.max(1, p - 1))} 
                      disabled={page === 1} 
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center px-4 text-sm text-slate-400">
                      Página {page} de {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                      disabled={page === totalPages} 
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </>
            )}
            
            {/* Related searches for SEO */}
            <RelatedSearches specialty={specialty} city={null} filters={filters} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function ProfessionalsBySpecialtyCity() {
  const { specialty, city } = useParams();
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [profRes, filtersRes] = await Promise.all([
          axios.get(`${API}/seo/profesionales/${specialty}/${city}?page=${page}&limit=12`),
          axios.get(`${API}/seo/available-filters`)
        ]);
        setProfessionals(profRes.data.professionals || []);
        setTotalPages(profRes.data.pages || 1);
        setTotal(profRes.data.total || 0);
        setData(profRes.data);
        setFilters(filtersRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [specialty, city, page]);

  // Validar specialty. Si no es válida → 404 con noindex.
  if (!SPECIALTY_LABELS[specialty]) return <NotFoundPage />;

  const specialtyLabel = data?.specialty_label || SPECIALTY_LABELS[specialty] || specialty;
  const cityLabel = data?.city_label || city;
  const specialtySingular = SPECIALTY_SINGULAR[specialty] || specialty;

  const seoTitle = `${specialtyLabel} en ${cityLabel} - Especialistas en Adicciones | Sin Adicciones`;
  const seoDescription = `Encuentra ${specialtyLabel.toLowerCase()} especializados en adicciones en ${cityLabel}, Chile. ${total} profesionales verificados con reseñas y contacto directo.`;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://sinadicciones.org/profesional/${specialty}/${city}`} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href={`https://sinadicciones.org/profesional/${specialty}/${city}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": seoTitle,
          "description": seoDescription,
          "url": `https://sinadicciones.org/profesional/${specialty}/${city}`,
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": total,
            "itemListElement": professionals.slice(0, 10).map((pro, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "item": {
                "@type": "Person",
                "name": pro.name,
                "jobTitle": specialtySingular,
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": cityLabel,
                  "addressCountry": "CL"
                }
              }
            }))
          }
        })}</script>
      
      <Header />
      <main className="pt-20">
        <section className="bg-slate-900 border-b border-slate-800 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumbs 
              specialty={specialty} 
              specialtyLabel={specialtyLabel}
              city={city}
              cityLabel={cityLabel}
            />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3" data-testid="seo-city-title">
              {specialtyLabel} en {cityLabel}
            </h1>
            <p className="text-slate-400 text-lg">
              {total > 0 
                ? `${total} ${specialtyLabel.toLowerCase()} especializados en adicciones en ${cityLabel}` 
                : `${specialtyLabel} especializados en tratamiento de adicciones en ${cityLabel}`
              }
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : professionals.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <Brain className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">
                  No se encontraron {specialtyLabel.toLowerCase()} en {cityLabel}
                </h3>
                <p className="text-slate-400 mb-6">Prueba buscando en otra ciudad</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to={`/profesional/${specialty}`}>Ver en todo Chile</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to="/profesionales">Ver todos los profesionales</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="professionals-seo-city-grid">
                  {professionals.map((pro) => (
                    <ProfessionalCard key={pro.user_id} pro={pro} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button 
                      variant="outline" 
                      onClick={() => setPage(p => Math.max(1, p - 1))} 
                      disabled={page === 1} 
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center px-4 text-sm text-slate-400">
                      Página {page} de {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                      disabled={page === totalPages} 
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </>
            )}
            
            {/* Related searches for SEO */}
            <RelatedSearches specialty={specialty} city={city} filters={filters} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
